// src/components/marketing/ContactPage.js
import React, { useState } from "react";
import axios from "../../config/axiosClient";
import SEO from "../common/SEO";

const ContactPage = () => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [website, setWebsite] = useState(""); // honeypot
  const [status, setStatus] = useState(null); // 'success' | 'error' | 'sending'

  const isValidEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
  const canSubmit = name.trim() && isValidEmail(email) && message.trim();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setStatus("sending");
    try {
      await axios.post("https://api.inertia7.com/api/v1/feedback/", {
        name,
        email,
        message,
        website, // honeypot
        page: window.location.pathname,
      });
      setStatus("success");
      setName("");
      setEmail("");
      setMessage("");
    } catch (err) {
      if (err.response?.status === 429) {
        setStatus("rate-limited");
      } else {
        setStatus("error");
      }
    }
  };

  return (
    <div className="container" style={{ maxWidth: 720, padding: "48px 16px" }}>
      <SEO title="Contact" description="Get in touch with the Inertia7 team." url="/contact" />
      <header style={{ textAlign: "center", marginBottom: 48 }}>
        <h1 style={{ marginBottom: 16, fontSize: "36px" }}>Contact Us</h1>
        <p style={{ color: "var(--muted, #64748b)", fontSize: "18px", lineHeight: "1.6" }}>
          Have a question, found a bug, or want to share feedback?
        </p>
      </header>

      {status === "success" ? (
        <div style={{
          textAlign: "center", padding: "40px 20px",
          background: "var(--bg-soft)", borderRadius: 8,
          border: "1px solid var(--border)",
        }}>
          <p style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>Thanks for reaching out.</p>
          <p style={{ color: "var(--muted)", fontSize: 14 }}>We'll get back to you if a response is needed.</p>
          <button
            onClick={() => setStatus(null)}
            style={{
              marginTop: 16, padding: "8px 24px", border: "1px solid var(--border)",
              borderRadius: 6, background: "var(--surface)", color: "var(--ink)",
              fontSize: 14, fontWeight: 600, cursor: "pointer",
            }}
          >
            Send another message
          </button>
        </div>
      ) : (
        <form onSubmit={handleSubmit}>
          <div style={{ display: "flex", gap: 12, marginBottom: 12 }}>
            <input
              type="text"
              placeholder="Name"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              style={inputStyle}
            />
            <input
              type="email"
              placeholder="Email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              style={inputStyle}
            />
          </div>

          {/* Honeypot — invisible to humans, bots auto-fill it */}
          <div style={{ position: "absolute", left: "-9999px" }} aria-hidden="true">
            <input
              type="text"
              name="website"
              tabIndex={-1}
              autoComplete="off"
              value={website}
              onChange={(e) => setWebsite(e.target.value)}
            />
          </div>

          <textarea
            placeholder="What's on your mind?"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            required
            rows={6}
            maxLength={5000}
            style={{
              width: "100%", padding: "10px 14px",
              border: "1px solid var(--border)", borderRadius: 6,
              background: "var(--surface)", color: "var(--ink)",
              fontSize: 14, fontFamily: "inherit",
              resize: "vertical", marginBottom: 12,
              boxSizing: "border-box",
            }}
          />

          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <span style={{ fontSize: 12, color: "var(--muted)" }}>
              {message.length}/5000
            </span>
            <button
              type="submit"
              disabled={status === "sending" || !canSubmit}
              style={{
                padding: "10px 32px", border: "none", borderRadius: 6,
                background: "var(--brand, #59c6c1)", color: "#fff",
                fontSize: 14, fontWeight: 600, cursor: status === "sending" ? "not-allowed" : "pointer",
                opacity: status === "sending" || !canSubmit ? 0.6 : 1,
              }}
            >
              {status === "sending" ? "Sending..." : "Send Feedback"}
            </button>
          </div>

          {status === "error" && (
            <p style={{ color: "var(--danger, #ef4444)", fontSize: 14, marginTop: 12 }}>
              Something went wrong. Please try again.
            </p>
          )}
          {status === "rate-limited" && (
            <p style={{ color: "var(--danger, #ef4444)", fontSize: 14, marginTop: 12 }}>
              Too many submissions. Please wait a minute and try again.
            </p>
          )}
        </form>
      )}

      <div style={{ textAlign: "center", marginTop: 48 }}>
        <p style={{ color: "var(--muted)", fontSize: 14 }}>
          You can also reach out on{" "}
          <a
            href="https://www.linkedin.com/in/dcamposliz/"
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: "var(--brand)", fontWeight: 600 }}
          >
            LinkedIn
          </a>
        </p>
      </div>
    </div>
  );
};

const inputStyle = {
  flex: 1, padding: "10px 14px",
  border: "1px solid var(--border)", borderRadius: 6,
  background: "var(--surface)", color: "var(--ink)",
  fontSize: 14, fontFamily: "inherit",
};

export default ContactPage;
